import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useMemo, useState } from "react";

import type { ManagedMcpServer } from "./useManagedMcpServers.ts";
import {
  type McpAccessLogEvent,
  type McpCallRecord,
  pairAccessLogEvents,
} from "./useMcpAccessLogs.ts";
import type { McpClient } from "./useMcpClients.ts";

type EventsResp = { events: McpAccessLogEvent[] };

const DEFAULT_MAX_RECORDS = 50;
const FETCH_LIMIT_MULTIPLIER = 4;

type Scope = {
  client?: Pick<McpClient, "id">;
  server?: Pick<ManagedMcpServer, "id">;
  sessionId?: string;
  maxRecords?: number;
};

/**
 * Issues a server-side `where`-filtered access-log query for the given scope
 * and pairs request + response events into `McpCallRecord`s. Each scope —
 * client, server, or session — translates to a single Firestore filter on
 * the indexed field, so the listener fan-out is bounded to the sidecar that
 * needs the records (no bulk context dependency, no client-side scan over a
 * tenant-wide event firehose).
 *
 * Fetches `maxRecords * FETCH_LIMIT_MULTIPLIER` raw events to give the
 * pairer headroom — one paired record needs both the request and the
 * response, which are two separate Firestore documents.
 */
export const useScopedMcpCallRecords = (
  scope: Scope
): {
  records: McpCallRecord[];
  loading: boolean;
  refetch: () => void;
} => {
  const authFetch = useAuthFetch();
  const [events, setEvents] = useState<McpAccessLogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const maxRecords = scope.maxRecords ?? DEFAULT_MAX_RECORDS;
  const clientId = scope.client?.id;
  const serverId = scope.server?.id;
  const { sessionId } = scope;

  useGuardedEffect(
    (cancellation) => async () => {
      if (!clientId && !serverId && !sessionId) {
        setEvents([]);
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: String(maxRecords * FETCH_LIMIT_MULTIPLIER),
        });
        if (clientId) params.set("clientId", clientId);
        if (serverId) params.set("serverId", serverId);
        if (sessionId) params.set("sessionId", sessionId);
        const res = await authFetch(
          `mcp-access-log/events?${params.toString()}`,
          { method: "GET", cancellation }
        );
        if (!res || cancellation.isCancelled) return;
        const body = (await res.json()) as EventsResp;
        if (cancellation.isCancelled) return;
        setEvents(body.events ?? []);
      } finally {
        if (!cancellation.isCancelled) setLoading(false);
      }
    },
    [authFetch, clientId, serverId, sessionId, maxRecords, reloadToken],
    useCallback(() => {
      setEvents([]);
      setLoading(false);
    }, [])
  );

  const records = useMemo(
    () => pairAccessLogEvents(events, maxRecords),
    [events, maxRecords]
  );

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);

  return { records, loading, refetch };
};
